import React, { FC, useState } from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { PlaceOutlined, KeyboardArrowDown } from '@mui/icons-material';

interface AlternativeLocationBadgeProps {
  locations: Array<{ locationId: string; locationName: string }>;
  onLocationChange: (locationId: string) => void;
}

/**
 * Shows a badge with alternative locations where this time+duration is available.
 * Data comes directly from appointment response - NO LOOKUPS needed!
 */
const AlternativeLocationBadge: FC<AlternativeLocationBadgeProps> = ({
  locations,
  onLocationChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleLocationSelect = (event: React.MouseEvent, locationId: string) => {
    event.stopPropagation();
    event.preventDefault();
    onLocationChange(locationId);
    handleClose();
  };

  // Don't show badge if no locations
  if (!locations || locations.length === 0) {
    return null;
  }

  // Show badge with first available location name
  const firstLocation = locations[0];
  const labelText = locations.length === 1 ? firstLocation.locationName : 'Alternative locations';

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '2px',
          cursor: 'pointer',
          padding: '4px 8px',
          transition: 'all 0.2s ease',
          maxWidth: '153px',
          '&:hover': {
            '& .badge-icon': {
              color: '#E55A2B',
            },
            '& .badge-text': {
              color: '#E55A2B',
              textDecoration: 'underline',
            },
            '& .badge-chevron': {
              color: '#E55A2B',
            },
          },
          ...(isOpen && {
            '& .badge-icon': {
              color: '#E55A2B',
            },
            '& .badge-text': {
              color: '#E55A2B',
              textDecoration: 'underline',
            },
            '& .badge-chevron': {
              color: '#E55A2B',
            },
          }),
        }}
      >
        <PlaceOutlined 
          className="badge-icon"
          sx={{ 
            fontSize: 16, 
            color: '#FF6B35',
            transition: 'color 0.2s ease',
            flexShrink: 0,
          }} 
        />
        <Typography
          className="badge-text"
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            fontWeight: 500,
            color: '#666',
            lineHeight: 1.2,
            transition: 'all 0.2s ease',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {labelText}
        </Typography>
        <KeyboardArrowDown 
          className="badge-chevron"
          sx={{ 
            fontSize: 14, 
            color: '#FF6B35',
            transition: 'color 0.2s ease',
            flexShrink: 0,
          }} 
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: '8px',
            boxShadow: '0px 2px 4px -2px rgba(0, 0, 0, 0.1), 0px 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '0.8px solid rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {locations.map((location) => (
          <MenuItem
            key={location.locationId}
            onClick={(event) => handleLocationSelect(event, location.locationId)}
            sx={{
              // py: 1.5,
              px: 2,
            }}
          >
            <ListItemIcon>
              <PlaceOutlined sx={{ fontSize: 20, color: '#FF6B35' }} />
            </ListItemIcon>
            <ListItemText
              primary={location.locationName}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500,
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AlternativeLocationBadge;
